import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { RoomType } from "../../types/C1_ModelTypes";
import { C1_FurnitureSvg, C1_FurnitureUprightContent } from "../C1_FurnitureSvg";
import Wardrobe from "./C1_Wardrobe";
import KitchenBaseCabinet from "./C1_KitchenBaseCabinet";

export const LivingCabinet: C1_FurnitureDefinition = {
  ...Wardrobe,
  id: "livingCabinet",
  description: { de: "Schrank", en: "Cabinet" },
  category: RoomType.Living,
  additionalCategories: [RoomType.Bedroom],
};

export const Sideboard: C1_FurnitureDefinition = {
  ...KitchenBaseCabinet,
  id: "sideboard",
  description: { de: "Kommode/Sideboard", en: "Sideboard" },
  category: RoomType.Living,
  defaultWidth: 1.6,
  defaultHeight: 0.4,
};

export const Bookshelf: C1_FurnitureDefinition = {
  id: "bookshelf",
  description: { de: "Bücherregal", en: "Bookshelf" },
  category: RoomType.Living,
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeLinejoin="miter">
      <rect x="4" y="2" width="16" height="20" strokeWidth="1.6" />
      <path d="M4 9h16M4 16h16M7 4v5M10 3v6M13 5l2 4M7 11v5M10 12v4M15 11v5M17 12v4M8 18v4M11 17v5M15 18v4" strokeWidth=".8" />
    </svg>
  ),
  defaultWidth: 1.2,
  defaultHeight: 0.3,
  originXPercentage: 50,
  originYPercentage: 0,
  wallDockable: true,
  autoRotate: true,
  grabsW: true,
  grabsH: true,
  properties: [],
  Renderer: ({ width, height, rotation = 0, PX, strokeWidth = 2, strokeColor = "#000000", fillColor = "#ffffff", showDetails }: C1_FurnitureRendererProps) => (
    <C1_FurnitureSvg width={width} height={height} rotation={rotation} PX={PX} viewBoxWidth={width} viewBoxHeight={height} originXPercentage={50} originYPercentage={0}>
      <rect width={width} height={height} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth * 2} strokeLinejoin="miter" />
      {showDetails && <g fill="none" stroke={strokeColor} strokeWidth={strokeWidth}>
        <path d={`M ${width / 3} 0 V ${height} M ${width * 2 / 3} 0 V ${height}`} />
        {[0.08, 0.15, 0.22, 0.42, 0.49, 0.75, 0.82, 0.89].map(offset => (
          <rect key={offset} x={width * offset} y={height * 0.15} width={width * 0.035} height={height * 0.7} />
        ))}
      </g>}
    </C1_FurnitureSvg>
  ),
};

export const CoffeeTable: C1_FurnitureDefinition = {
  id: "coffeeTable",
  description: { de: "Couchtisch", en: "Coffee table" },
  category: RoomType.Living,
  icon: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="miter" /></svg>,
  defaultWidth: 0.8,
  defaultHeight: 0.8,
  originXPercentage: 50,
  originYPercentage: 50,
  grabsW: true,
  grabsH: true,
  properties: [],
  Renderer: ({ width, height, rotation = 0, PX, strokeWidth = 2, strokeColor = "#000000", fillColor = "#ffffff" }: C1_FurnitureRendererProps) => (
    <C1_FurnitureSvg width={width} height={height} rotation={rotation} PX={PX} viewBoxWidth={width} viewBoxHeight={height} originXPercentage={50} originYPercentage={50}>
      <rect width={width} height={height} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth * 2} strokeLinejoin="miter" />
    </C1_FurnitureSvg>
  ),
};

const FLOOR_LAMP_SYMBOL_PATH = "M8 3h8l3 7H5zM12 10v11M7 21h10";

export const FloorLamp: C1_FurnitureDefinition = {
  id: "floorLamp",
  description: { de: "Stehlampe", en: "Floor lamp" },
  category: RoomType.Living,
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d={FLOOR_LAMP_SYMBOL_PATH} />
    </svg>
  ),
  defaultWidth: 0.4,
  defaultHeight: 0.4,
  grabsW: false,
  grabsH: false,
  originXPercentage: 50,
  // The fixed 40 cm footprint starts 50 cm inside the wall.
  originYPercentage: -125,
  wallDockable: true,
  autoRotate: true,
  properties: [],
  Renderer: ({ width, height, rotation = 0, ownerRotation = rotation, ownerReflect = false, PX, strokeWidth = 2, strokeColor = "#000000", fillColor = "#ffffff", wallDocked = false }: C1_FurnitureRendererProps) => (
    <C1_FurnitureSvg width={width} height={height} rotation={rotation} PX={PX} viewBoxWidth={width} viewBoxHeight={height} originXPercentage={50} originYPercentage={-125}>
      {wallDocked && (
        <path
          d={`M ${width / 2} 0 C ${width * 0.2} ${-height * 0.2}, ${width * 0.2} ${-height * 0.425}, ${width / 2} ${-height * 0.625} S ${width * 0.8} ${-height * 1.05}, ${width / 2} ${-height * 1.25}`}
          fill="none" stroke={strokeColor} strokeWidth={strokeWidth * 2}
        />
      )}
      <ellipse cx={width / 2} cy={height / 2} rx={width / 2} ry={height / 2} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth * 2} />
      <C1_FurnitureUprightContent anchorX={width / 2} anchorY={height / 2}
        ownerRotation={Number(ownerRotation)} ownerReflect={Boolean(ownerReflect)}>
        <svg x={width * 0.15} y={height * 0.15} width={width * 0.7} height={height * 0.7} viewBox="0 0 24 24" fill="none" stroke={strokeColor}>
          <path d={FLOOR_LAMP_SYMBOL_PATH} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" />
        </svg>
      </C1_FurnitureUprightContent>
    </C1_FurnitureSvg>
  ),
};
